import React from 'react';
import styles from './Card.module.css';

interface FeatureCardProps {
  
  title: string;
  description: string;
  animationDelay?: number;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  description,
  animationDelay = 0,
}) => {
  return (
    <div
      className={styles.featureCard}
      style={{ animationDelay: `${animationDelay}s` }}
    >
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
};

interface TeamCardProps {
  avatar: string;
  name: string;
  role: string;
  detail?: string;
  linkedinUrl?: string;
}

export const TeamCard: React.FC<TeamCardProps> = ({
  avatar,
  name,
  role,
  detail,
  linkedinUrl,
}) => {
  const isImage = avatar.startsWith('/') || avatar.includes('.jpg') || avatar.includes('.png') || avatar.includes('.jpeg');

  return (
    <div className={styles.teamCard}>
      {isImage ? (
        <img src={avatar} alt={name} className={styles.teamAvatarImg} />
      ) : (
        <div className={styles.teamAvatar}>{avatar}</div>
      )}
      <div className={styles.teamNameRow}>
        <h4>{name}</h4>
        {linkedinUrl && (
          <a
            href={linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.linkedinLink}
            aria-label={`${name}'s LinkedIn profile`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
        )}
      </div>
      <p className={styles.teamRole}>{role}</p>
      {detail && <p className={styles.teamDetail}>{detail}</p>}
    </div>
  );
};

interface ResourceCardProps {
  icon: string;
  title: string;
  description: string;
  actionLabel: string;
  actionHref?: string;
}

export const ResourceCard: React.FC<ResourceCardProps> = ({
  icon,
  title,
  description,
  actionLabel,
  actionHref = '#',
}) => {
  return (
    <div className={styles.resourceCard}>
      <div className={styles.resourceIcon}>{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
      <a href={actionHref} className={styles.resourceAction}>
        {actionLabel}
      </a>
    </div>
  );
};

interface ContactCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel: string;
  actionHref?: string;
}

export const ContactCard: React.FC<ContactCardProps> = ({
  icon,
  title,
  description,
  actionLabel,
  actionHref = '#',
}) => {
  return (
    <div className={styles.contactCard}>
      <div className={styles.contactHeader}>
        <div className={styles.contactIcon}>{icon}</div>
        <h3>{title}</h3>
      </div>
      <p>{description}</p>
      <a href={actionHref} className={styles.contactAction}>
        {actionLabel}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14"/>
          <path d="M12 5l7 7-7 7"/>
        </svg>
      </a>
    </div>
  );
};

interface MetricCardProps {
  label: string;
  value: string;
  highlight?: boolean;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  highlight = false,
}) => {
  return (
    <div className={styles.metricCard}>
      <div className={styles.metricLabel}>{label}</div>
      <div className={`${styles.metricValue} ${highlight ? styles.gold : ''}`}>
        {value}
      </div>
    </div>
  );
};
